import api, { type ApiResponse } from '@/lib/api';
import { toLocalDateString } from '@/lib/utils';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type { ConversationFilters } from '@/modules/shadow/components/conversation-filters';

export async function requestShadowReportEmail(options: {
  email: string;
  reportType: 'raw' | 'summary';
  filters: ConversationFilters & { minAccuracy?: number; category?: string };
  /** The selected brand; absent = account-wide. */
  scope?: { bu_id?: string };
}) {
  const { email, reportType, filters, scope } = options;

  const payload: Record<string, unknown> = {
    email,
    reportType
  };

  // Send as local YYYY-MM-DD strings, not UTC ISO strings.
  // toISOString() converts to UTC, which shifts the date for users in non-UTC timezones
  // (e.g. UTC+5:30 selecting April 5 would send "April 4T18:30Z").
  if (filters.startDate) payload.startDate = toLocalDateString(filters.startDate);
  if (filters.endDate) payload.endDate = toLocalDateString(filters.endDate);

  // Lets the backend resolve the dates above in the browser's own zone instead of UTC.
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (timezone) payload.timezone = timezone;

  if (filters.ruleId) payload.ruleId = filters.ruleId;
  if (filters.minAccuracy != null) payload.minAccuracy = filters.minAccuracy;
  if ('category' in filters && filters.category) payload.category = filters.category;
  if (filters.rulesAndKbs && filters.rulesAndKbs.length > 0) {
    payload.rulesAndKbs = filters.rulesAndKbs;
  }
  if (filters.responseQuality && filters.responseQuality.length > 0) {
    payload.responseQuality = filters.responseQuality;
  }
  if (filters.contactSearch && filters.contactSearch.trim()) {
    payload.contactSearch = filters.contactSearch.trim();
  }
  if (filters.conversationIdSearch) {
    payload.conversationId = filters.conversationIdSearch;
  }
  if (scope?.bu_id) payload.bu_id = scope.bu_id;

  const response = await api.post<ApiResponse<{ queued: boolean }>>(
    API_ENDPOINTS.REPORTS.SHADOW_EMAIL,
    payload
  );

  return response.data.data;
}

